<template>
  <v-container fluid class="pa-0">
    <div class="classification-report-container px-3 mb-10">
      <report-condition
        :searchCondition.sync="searchCondition"
        @updateDataChart="updateDataChart"
        :displaySourceItems="CLASSIFICATION_DISPLAY_ITEM_VALUES"
      />
      <!-- CHART -->
      <fieldset class="zone-block pa-3 my-2 mw-900">
        <v-row>
          <div class="pre-title my-4 ml-3">
            <span>{{ $t("label.dataGraph") }}</span>
          </div>
        </v-row>
        <v-row
          v-if="displayCharts.length > 0"
          style="min-width: 1200px; overflow: auto"
        >
          <v-col cols="1" class="text-center" style="margin: auto">
            <v-btn
              fab
              dark
              :disabled="selectedIdx === 0"
              class="elevation-0 btn-change-slice"
              large
              @click="previousChart"
              color="success"
            >
              <v-icon large class="white--text">{{
                $t("ic.mdiChevronLeft")
              }}</v-icon>
            </v-btn>
          </v-col>
          <v-col
            cols="10"
            class="px-0 d-flex text-center justify-content-center"
            :key="componentKey"
          >
            <v-col
              v-for="(displayChart, idx) in displayCharts"
              :key="`chart-${idx}`"
              class="justify-content-center"
              :cols="colSpan"
            >
              <report-column-chart :displayChart="displayChart" />
            </v-col>
          </v-col>
          <v-col cols="1" class="text-center" style="margin: auto">
            <v-btn
              fab
              dark
              class="elevation-0 btn-change-slice"
              large
              :disabled="isLastChart"
              color="success"
              @click="nextChart"
            >
              <v-icon large class="white--text">{{
                $t("ic.mdiChevronRight")
              }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" class="text-center" style="margin: auto">
            <v-btn
              fab
              dark
              class="elevation-0"
              style="cursor: auto"
              large
              color="secondary"
            >
              <v-icon large class="white--text">{{
                $t("ic.mdiBarChart")
              }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </fieldset>

      <!-- DATA TABLE -->
      <fieldset class="zone-block pa-3 my-2 m-w-800">
        <v-row>
          <div class="pre-title my-4 ml-3">
            <span>{{ $t("label.dataTable") }}</span>
          </div>
        </v-row>
        <v-row v-if="displayCharts.length > 0">
          <v-col cols="12" :key="tblKey" :id="`tbl-data`">
            <classification-report-tbl
              ref="tblClassificationReport"
              :request.sync="searchCondition"
              :selectedDate="selectedDate"
              :pagination="pagination"
              :reportLayout.sync="reportLayout"
            />
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" class="text-center" style="margin: auto">
            <v-btn
              fab
              dark
              class="elevation-0"
              style="cursor: auto"
              large
              variant="outlined"
              color="secondary"
            >
              <v-icon large class="white--text">{{
                $t("ic.mdiTableLarge")
              }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </fieldset>
    </div>
  </v-container>
</template>

<script>
import { SET_LOADING, SET_PAGE_HEADER } from "@/store/mutations.type";
import ReportCondition from "@/components/report/ReportCondition";
import { SCREEN_TITLES } from "@/common/report.constant.js";
import {
  getJPDateFormat,
  getFieldNameChartById,
  CLASSIFICATION_DISPLAY_ITEM_VALUES,
} from "@/common/constant";
import { SCREEN } from "@/common/auth.const";
import ReportColumnChart from "@/components/report/ReportColumnChart.vue";
import ClassificationReportTbl from "@/components/report/ClassificationReportTbl.vue";
import { GET_CLASSIFICATION_REPORT_DATA } from "@/api/report";
import { ALERT } from "@/common/utilities";
import { scroll2BottomByElementId } from "@/common/common-function";

export default {
  name: "ClassificationReport",
  components: {
    ReportCondition,
    ReportColumnChart,
    ClassificationReportTbl,
  },
  data() {
    return {
      searchCondition: {
        referenceStartDate: null,
        referenceEndDate: null,
        compareStartDate: null,
        compareEndDate: null,
        storeCode: null,
        majorCodes: [],
        middleCodes: [],
        displayItems: [],
      },
      colSpan: 3,
      displayCharts: [],
      componentKey: 0,
      tblKey: 0,
      reportLayout: {
        selectedReportDate: null,
        compareReportDate: null,
        reportIds: [],
        dataSeriesTbl: [],
        page: 1,
        limit: 10,
        offset: 0,
      },
      pagination: {
        page: 1,
        itemsPerPage: 10,
        pageStart: 0,
        pageStop: 0,
        pageCount: 0,
        itemsLength: 0,
      },
      getJPDateFormat,
      getFieldNameChartById,
      selectedIdx: 0,
      dataChart: [],
      dataReportTbl: [],
      selectedDate: {
        referenceDate: "",
        compareDate: "",
      },
      scroll2BottomByElementId,
      CLASSIFICATION_DISPLAY_ITEM_VALUES: CLASSIFICATION_DISPLAY_ITEM_VALUES,
    };
  },
  computed: {
    isLastChart() {
      return (
        this.selectedIdx >=
        (this.dataChart.length > 2
          ? this.dataChart.length - 1
          : this.dataChart.length - 1)
      );
    },
  },
  methods: {
    back() {
      this.$router.push({
        name: SCREEN.HOME.NAME,
      });
    },
    async updateDataChart() {
      try {
        this.$store.commit(SET_LOADING, [true, true]);
        let { data } = await GET_CLASSIFICATION_REPORT_DATA(
          this.searchCondition,
        );
        if (!data.lstReportChart || data.lstReportChart.length === 0) {
          ALERT.WARNING(this.$i18n.t("msg.noDataAvailable"));
          this.$store.commit(SET_LOADING, [false, true]);
          return;
        }
        this.selectedIdx = 0;
        this.dataChart = data.lstReportChart;

        this.dataReportTbl = data.lstReportTable;
        this.updateReportLayout(
          this.dataChart[this.selectedIdx],
          this.dataReportTbl,
        );
        this.updateDataSeries(this.dataChart[this.selectedIdx]);
        this.reportLayout.page = 1;
        this.reportLayout.limit = 10;
        this.scroll2Bottom();
        this.updateColSpans();
        this.$store.commit(SET_LOADING, [false, true]);
        this.componentKey += 1;
      } catch (error) {
        this.$store.commit(SET_LOADING, [false, true]);
        console.log(error);
      }
    },
    updateDataSeries(selectedDataChart) {
      this.displayCharts = [];
      let referenceDate =
        selectedDataChart.chooseReportResponse.createdSynchron;
      let compareDate = selectedDataChart.compareReportResponse.createdSynchron;
      this.reportLayout.reportIds.forEach((reportId) => {
        let dataSeries = [];
        dataSeries.push({
          name: getJPDateFormat(referenceDate, "-"),
          data: [
            selectedDataChart.chooseReportResponse[
              getFieldNameChartById(reportId)
            ],
          ],
        });
        dataSeries.push({
          name: getJPDateFormat(compareDate, "-"),
          data: [
            selectedDataChart.compareReportResponse[
              getFieldNameChartById(reportId)
            ],
          ],
        });
        this.displayCharts.push({
          dataSeries: dataSeries,
          chartId: reportId,
        });

        //update selected date
        this.selectedDate.referenceDate = referenceDate;
        this.selectedDate.compareDate = compareDate;
      });
    },
    updateReportLayout(selectedDataChart, dataTbl) {
      this.reportLayout.reportIds = [
        ...this.searchCondition.displayItems.sort(),
      ];
      this.reportLayout.selectedReportDate = getJPDateFormat(
        selectedDataChart.chooseReportResponse.createdSynchron,
        "-",
      );
      this.reportLayout.compareReportDate = getJPDateFormat(
        selectedDataChart.compareReportResponse.createdSynchron,
        "-",
      );

      let syncDate = selectedDataChart.chooseReportResponse.createdSynchron;
      let lstReportTableByMajors = dataTbl.filter(
        (item) =>
          item.lstReportTableByMajor.length > 0 &&
          item.lstReportTableByMajor[0].createdSynchron == syncDate,
      );
      if (lstReportTableByMajors.length <= 0) {
        this.reportLayout.dataSeriesTbl = [];
        this.tblKey += 1;
        return;
      }
      let selectedDataTbl = lstReportTableByMajors[0];

      this.reportLayout.dataSeriesTbl = [
        ...selectedDataTbl.lstReportTableByMajor,
      ];
      this.reportLayout.count = this.reportLayout.dataSeriesTbl.count;
      let compounndRowData = {
        orderQuantity: 0,
        orderQuantityCompare: 0,
        totalPrice: 0,
        totalPriceCompare: 0,
        totalSaleMoney: 0,
        totalSaleMoneyCompare: 0,
        cancelOrderAmount: 0,
        cancelOrderAmountCompare: 0,
      };
      if (
        selectedDataTbl.lstReportTableByMajor &&
        selectedDataTbl.lstReportTableByMajor.length > 0
      ) {
        selectedDataTbl.lstReportTableByMajor.forEach((item) => {
          compounndRowData.orderQuantity += item.orderQuantity;
          compounndRowData.orderQuantityCompare += item.orderQuantityCompare;
          compounndRowData.totalPrice += item.totalPrice;
          compounndRowData.totalPriceCompare += item.totalPriceCompare;
          compounndRowData.totalSaleMoney += item.totalSaleMoney;
          compounndRowData.totalSaleMoneyCompare += item.totalSaleMoneyCompare;
          compounndRowData.cancelOrderAmount += item.cancelOrderAmount;
          compounndRowData.cancelOrderAmountCompare +=
            item.cancelOrderAmountCompare;
        });
      }
      this.reportLayout.dataSeriesTbl.push(compounndRowData);
      this.pagination.itemsPerPage = this.reportLayout.dataSeriesTbl.length;
      this.tblKey += 1;
    },
    updateColSpans() {
      this.colSpan = 0;
      this.colSpan = 12 / this.displayCharts.length;
      this.componentKey += 1;
    },
    scroll2Bottom() {
      this.scroll2BottomByElementId("tbl-data");
    },
    nextChart() {
      if (this.selectedIdx >= this.dataChart.length) {
        return;
      }
      this.selectedIdx += 1;
      this.updateDataSeries(this.dataChart[this.selectedIdx]);
      this.updateReportLayout(
        this.dataChart[this.selectedIdx],
        this.dataReportTbl,
      );
      this.componentKey += 1;
    },
    previousChart() {
      if (this.selectedIdx <= 0) {
        return;
      }
      this.selectedIdx -= 1;
      this.updateDataSeries(this.dataChart[this.selectedIdx]);
      this.updateReportLayout(
        this.dataChart[this.selectedIdx],
        this.dataReportTbl,
      );
      this.componentKey += 1;
    },
  },
  created() {
    this.$store.commit(SET_PAGE_HEADER, [
      SCREEN_TITLES.CLASSIFICATION_REPORT,
      SCREEN.HOME.NAME,
      this.$t("button.back"),
      null,
      this.back,
    ]);
  },
};
</script>

<style lang="scss">
.classification-report-container {
  height: 100%;
  .btn-change-slice:hover {
    transform: scale(1.25);
  }
}
</style>
